// frontend/src/App.js
import React, { Suspense, lazy, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "sonner";
import "./App.css";

import { HelmetProvider } from "react-helmet-async";
import AnalyticsListener from "./components/AnalyticsListener";

import { initGA } from "./utils/analytics";

import WhatsAppButton from "./components/WhatsAppButton";
import CallNowButton from "./components/CallNowButton";
import PremiumNavbar from "./components/PremiumNavbar";
import Footer from "./components/Footer";

const Chatbot = lazy(() => import("./components/Chatbot"));
const LeadPopup = lazy(() => import("./components/LeadPopup"));

// Pages
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import UserDashboard from "./pages/UserDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import LoanApplication from "./pages/LoanApplication";
import CreditCards from "./pages/CreditCards";

import Tools from "./pages/Tools";
import Investments from "./pages/Investments";
import SIPs from "./pages/SIPs";
import MutualFunds from "./pages/MutualFunds";
import Portfolio from "./pages/Portfolio";
import Reports from "./pages/Reports";

// Legal Pages
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfUse from "./pages/TermsOfUse";
import RefundPolicy from "./pages/RefundPolicy";
import Contact from "./pages/Contact";

const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading, isAdmin } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && !isAdmin()) return <Navigate to="/dashboard" replace />;

  return children;
};

function AppRoutes() {
  const { user, isAdmin } = useAuth();

  return (
    <>
      <PremiumNavbar />

      <Routes>
        {/* Public */}
        <Route path="/" element={<Landing />} />
        <Route path="/tools" element={<Tools />} />
        <Route path="/investments" element={<Investments />} />
        <Route path="/sips" element={<SIPs />} />
        <Route path="/mutual-funds" element={<MutualFunds />} />
        <Route path="/portfolio" element={<Portfolio />} />
        <Route path="/reports" element={<Reports />} />

        {/* Legal */}
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfUse />} />
        <Route path="/refund" element={<RefundPolicy />} />
        <Route path="/contact" element={<Contact />} />

        {/* Auth */}
        <Route
          path="/login"
          element={
            user ? (
              <Navigate to={isAdmin() ? "/admin" : "/dashboard"} replace />
            ) : (
              <Login />
            )
          }
        />
        <Route
          path="/register"
          element={
            user ? (
              <Navigate to={isAdmin() ? "/admin" : "/dashboard"} replace />
            ) : (
              <Register />
            )
          }
        />

        {/* Protected */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <UserDashboard />
            </ProtectedRoute>
          }
        />

        <Route
          path="/apply-loan"
          element={
            <ProtectedRoute>
              <LoanApplication />
            </ProtectedRoute>
          }
        />

        <Route
          path="/credit-cards"
          element={
            <ProtectedRoute>
              <CreditCards />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin"
          element={
            <ProtectedRoute adminOnly={true}>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      <Footer />
    </>
  );
}

export default function App() {
  const [loadHeavyWidgets, setLoadHeavyWidgets] = useState(false);

  // ✅ Init GA once (needed before AnalyticsListener starts tracking route changes)
  useEffect(() => {
    initGA();
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setLoadHeavyWidgets(true), 3000);
    return () => clearTimeout(t);
  }, []);

  return (
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors />
          <AnalyticsListener />
          <AppRoutes />

          <WhatsAppButton />
          <CallNowButton />

          {loadHeavyWidgets && (
            <Suspense fallback={null}>
              <Chatbot />
              <LeadPopup />
            </Suspense>
          )}
        </BrowserRouter>
      </AuthProvider>
    </HelmetProvider>
  );
            }
